import React, { useEffect, useRef, useState } from 'react';
// Import directly from source files to avoid circular dependency with barrel export
import {
	faCartPlus,
	faMinus,
	faPlus,
	faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { useThemeColors } from '../providers/ThemeProvider';
import { Icon } from './base/Icon';
import { Text } from './base/Text';

interface AddToCartButtonProps {
	disabled?: boolean;
	initialQuantity?: number;
	maxQuantity?: number;
	cartQuantity?: number; // Quantity from existing cart
	clientId?: number; // Client ID for API calls
	itemId?: number; // Item ID for API calls
	price?: number; // Item price for API calls
	perOrderLimit?: number; // Per-order limit for the item
	isCartLoading?: boolean; // Whether cart data is still loading
	onCartChange?: (operation?: {
		type: 'add' | 'update' | 'remove';
		itemId: number;
		quantity?: number;
		price?: number;
	}) => Promise<unknown>; // Enhanced callback to handle optimistic updates
	onCartUpdate?: (cartData: unknown) => void; // Callback with updated cart data from API
	// Customizable text props
	addButtonText?: string; // Text for the add button (default: "Add to Cart")
	addingText?: string; // Text while adding (default: "Adding...")
	// Pending price support - allows checking for pending price at click time
	getPendingPrice?: (itemId: number) => number | undefined;
}

export const AddToCartButton = React.memo(
	({
		disabled = false,
		initialQuantity = 1,
		maxQuantity,
		cartQuantity,
		clientId,
		itemId,
		price,
		isCartLoading = false,
		onCartChange,
		onCartUpdate,
		addButtonText = 'Add',
		addingText = 'Adding...',
		getPendingPrice,
	}: AddToCartButtonProps) => {
		const colors = useThemeColors();
		const [showQuantitySelector, setShowQuantitySelector] = useState(
			cartQuantity ? cartQuantity > 0 : false,
		);
		const [quantity, setQuantity] = useState(
			cartQuantity && cartQuantity > 0 ? cartQuantity : initialQuantity,
		);
		const [loading, setLoading] = useState(false);
		const [feedback, setFeedback] = useState<'success' | 'error' | null>(null);
		const [isMounted, setIsMounted] = useState(false);
		const [updating, setUpdating] = useState(false);
		const prevCartQuantityRef = useRef<number | undefined>(undefined);
		const feedbackTimerRef = useRef<NodeJS.Timeout | null>(null);
		const lastOperationTimeRef = useRef<number>(0);

		// Helper function to clear feedback
		const clearFeedback = () => {
			if (feedbackTimerRef.current) {
				clearTimeout(feedbackTimerRef.current);
				feedbackTimerRef.current = null;
			}
			setFeedback(null);
		};

		// Track component mount status
		useEffect(() => {
			setIsMounted(true);
			return () => {
				setIsMounted(false);
				// Clear feedback timer on unmount
				if (feedbackTimerRef.current) {
					clearTimeout(feedbackTimerRef.current);
				}
				// Component cleanup
			};
		}, []);

		// Update when cart data changes
		useEffect(() => {
			if (cartQuantity !== prevCartQuantityRef.current) {
				prevCartQuantityRef.current = cartQuantity;

				if (cartQuantity !== undefined && !loading && !updating) {
					setShowQuantitySelector(cartQuantity > 0);
					setQuantity(cartQuantity > 0 ? cartQuantity : initialQuantity);
				}
			}
		}, [cartQuantity, loading, updating, initialQuantity]);

		const handleAddToCartClick = async () => {
			// Check for pending price first (set by ShopPriceInput), then fall back to price prop
			const pendingPrice =
				itemId != null && getPendingPrice != null
					? getPendingPrice(itemId)
					: undefined;
			const effectivePrice = pendingPrice ?? price ?? 0;

			if (!clientId || !itemId || isNaN(effectivePrice)) {
				console.error(
					'AddToCartButton: Missing required data for cart operation',
					{
						clientId,
						itemId,
						price,
						effectivePrice,
						priceIsNaN: isNaN(effectivePrice),
					},
				);
				setShowQuantitySelector(true);
				return;
			}

			setUpdating(true);
			setLoading(true);
			setShowQuantitySelector(true);
			setQuantity(initialQuantity);

			// Emit optimistic update event for immediate validation feedback
			if (typeof window !== 'undefined' && itemId) {
				window.dispatchEvent(
					new CustomEvent('optimistic-cart-update', {
						detail: {
							itemId,
							quantity: initialQuantity,
							price: effectivePrice,
						},
					}),
				);
			}

			try {
				let response;

				if (onCartChange) {
					response = await onCartChange({
						type: 'add',
						itemId,
						quantity: initialQuantity,
						price: effectivePrice,
					});
				} else {
					// This shared component requires onCartChange callback
					throw new Error(
						'AddToCartButton requires onCartChange callback when used as shared component',
					);
				}

				if (response?.success) {
					if (response.data && onCartUpdate) {
						onCartUpdate(response.data);
					}
				} else {
					console.error('AddToCartButton: Add to cart failed', {
						itemId,
						response,
					});
					setFeedback('error');
					setTimeout(() => {
						if (isMounted) {
							setFeedback(null);
						}
					}, 3000);
				}
			} catch (error: any) {
				// Check if this is a cancelled operation (replaced by newer one)
				const isCancelled = error?.message?.includes('Operation cancelled');

				console.error('AddToCartButton: Exception during add to cart', {
					itemId,
					error: error?.message || error,
					isCancelled,
				});

				if (!isCancelled) {
					// Only show error feedback for actual failures, not cancellations
					setFeedback('error');
					setTimeout(() => {
						if (isMounted) {
							setFeedback(null);
						}
					}, 3000);
				}
			} finally {
				setUpdating(false);
				setLoading(false);
			}
		};

		const handleQuantityChange = (value: string) => {
			const num = parseInt(value) || 0;

			const newQuantity = Math.max(0, num);

			// Clear any existing feedback when user starts new interaction
			clearFeedback();

			// Update UI immediately for responsiveness
			setQuantity(newQuantity);

			// If quantity becomes 0, remove from cart immediately (no debounce)
			if (newQuantity === 0) {
				handleRemoveFromCart();
			} else {
				// Use debounced update for quantity changes
				debouncedUpdateCartQuantity(newQuantity);
			}
		};

		const debouncedUpdateCartQuantity = (newQuantity: number) => {
			// Clear feedback when starting a new operation
			clearFeedback();

			// Call updateCartQuantity immediately - the queue handles its own debouncing
			updateCartQuantity(newQuantity);
		};

		const updateCartQuantity = async (newQuantity: number) => {
			const effectivePrice = price ?? 0;

			if (!itemId || isNaN(effectivePrice)) {
				return;
			}

			// Track when this operation started
			const operationStartTime = Date.now();
			lastOperationTimeRef.current = operationStartTime;

			setUpdating(true);

			try {
				let response;
				if (onCartChange) {
					response = await onCartChange({
						type: 'update',
						itemId,
						quantity: newQuantity,
						price: effectivePrice,
					});
				} else {
					// This shared component requires onCartChange callback
					throw new Error(
						'AddToCartButton requires onCartChange callback when used as shared component',
					);
				}

				// Only show feedback if this is still the most recent operation
				if (lastOperationTimeRef.current === operationStartTime) {
					if (response?.success) {
						// Don't show success feedback for rapid updates - it's too noisy
						// Only show feedback for errors
						if (response.data && onCartUpdate) {
							onCartUpdate(response.data);
						}
					} else {
						setFeedback('error');
						setQuantity(cartQuantity || initialQuantity);
						feedbackTimerRef.current = setTimeout(() => {
							if (isMounted) {
								setFeedback(null);
							}
						}, 3000);
					}
				}
			} catch (error: any) {
				// Check if this is a cancelled operation (replaced by newer one)
				const isCancelled = error?.message?.includes('Operation cancelled');

				// Only show feedback if this is still the most recent operation and not cancelled
				if (
					lastOperationTimeRef.current === operationStartTime &&
					!isCancelled
				) {
					setFeedback('error');
					setQuantity(cartQuantity || initialQuantity);
					feedbackTimerRef.current = setTimeout(() => {
						if (isMounted) {
							setFeedback(null);
						}
					}, 3000);
				}
			} finally {
				// Only clear updating state if this is the most recent operation
				if (lastOperationTimeRef.current === operationStartTime) {
					setUpdating(false);
				}
			}
		};

		const handleRemoveFromCart = async () => {
			if (!itemId) {
				setShowQuantitySelector(false);
				setQuantity(initialQuantity);
				return;
			}

			setUpdating(true);
			setShowQuantitySelector(false);
			setQuantity(initialQuantity);

			try {
				let response;
				if (onCartChange) {
					response = await onCartChange({
						type: 'remove',
						itemId,
					});
				} else {
					// This shared component requires onCartChange callback
					throw new Error(
						'AddToCartButton requires onCartChange callback when used as shared component',
					);
				}

				if (response?.success) {
					if (response.data && onCartUpdate) {
						onCartUpdate(response.data);
					}
				} else {
					setShowQuantitySelector(true);
					setQuantity(cartQuantity || initialQuantity);
					setFeedback('error');
					setTimeout(() => {
						if (isMounted) {
							setFeedback(null);
						}
					}, 3000);
				}
			} catch (error: any) {
				// Check if this is a cancelled operation (replaced by newer one)
				const isCancelled = error?.message?.includes('Operation cancelled');

				if (!isCancelled) {
					// Only show error feedback for actual failures, not cancellations
					setShowQuantitySelector(true);
					setQuantity(cartQuantity || initialQuantity);
					setFeedback('error');
					setTimeout(() => {
						if (isMounted) {
							setFeedback(null);
						}
					}, 3000);
				}
			} finally {
				setUpdating(false);
			}
		};

		const incrementQuantity = async () => {
			const newQuantity = quantity + 1;
			const effectivePrice = price ?? 0;

			// Clear any existing feedback when user starts new interaction
			clearFeedback();

			// Update UI immediately for responsiveness
			setQuantity(newQuantity);

			// Emit optimistic update event for immediate validation feedback
			if (typeof window !== 'undefined' && itemId) {
				window.dispatchEvent(
					new CustomEvent('optimistic-cart-update', {
						detail: { itemId, quantity: newQuantity, price: effectivePrice },
					}),
				);
			}

			// Use debounced update for increment
			debouncedUpdateCartQuantity(newQuantity);
		};

		const decrementQuantity = async () => {
			const newQuantity = Math.max(0, quantity - 1);
			const effectivePrice = price ?? 0;

			// Clear any existing feedback when user starts new interaction
			clearFeedback();

			// Update UI immediately for responsiveness
			setQuantity(newQuantity);

			// Emit optimistic update event for immediate validation feedback
			if (typeof window !== 'undefined' && itemId) {
				window.dispatchEvent(
					new CustomEvent('optimistic-cart-update', {
						detail: { itemId, quantity: newQuantity, price: effectivePrice },
					}),
				);
			}

			// If quantity becomes 0, remove from cart immediately (no debounce)
			if (newQuantity === 0) {
				await handleRemoveFromCart();
			} else {
				// Use debounced update for decrement
				debouncedUpdateCartQuantity(newQuantity);
			}
		};

		// Fixed width container
		const containerStyle = { width: 100, height: 32 };

		// Show loading state while cart is loading
		if (isCartLoading) {
			return (
				<div
					style={containerStyle}
					className="flex items-center justify-center"
				>
					<div
						className={`${colors.classes.bgSecondary} rounded-lg animate-pulse`}
						style={{ width: 96, height: 32 }}
					/>
				</div>
			);
		}

		// Show "Add to Cart" button initially
		if (!showQuantitySelector) {
			return (
				<div
					style={containerStyle}
					className="flex items-center justify-center"
				>
					<button
						onClick={handleAddToCartClick}
						disabled={disabled || loading}
						className={`${colors.classes.primaryButton} rounded-lg px-3 flex items-center justify-center transition-colors`}
						style={{
							width: 96,
							height: 32,
							opacity: disabled || loading ? 0.5 : 1,
						}}
					>
						<Icon icon={faCartPlus} size={14} className="text-white" />
						<Text className="text-white font-semibold text-sm ml-2 truncate">
							{loading ? addingText : addButtonText}
						</Text>
					</button>
				</div>
			);
		}

		// Determine border color based on state
		const getBorderColor = () => {
			if (feedback === 'success') {
				return colors.classes.borderSuccess;
			}
			if (feedback === 'error') {
				return colors.classes.borderError;
			}
			// Don't change border color during updates - keep default
			return colors.classes.borderDefault;
		};

		// Show quantity selector
		return (
			<div style={containerStyle} className="flex items-center justify-center">
				<div
					className={`flex items-center rounded-lg ${colors.classes.bgPrimary} border ${getBorderColor()}`}
					style={{ height: 32, width: 96 }}
				>
					<button
						onClick={decrementQuantity}
						className={`flex-1 h-8 flex items-center justify-center bg-transparent hover:bg-black/5 dark:hover:bg-white/10 transition-colors rounded-l-lg touch-manipulation`}
						disabled={quantity <= 0}
					>
						<Icon
							icon={quantity === 1 ? faTrash : faMinus}
							size={10}
							className={
								quantity <= 0 || disabled
									? colors.classes.textSecondary
									: colors.classes.textPrimary
							}
						/>
					</button>
					<div className="relative" style={{ width: 36 }}>
						<input
							type="number"
							value={quantity.toString()}
							onChange={(e) => {
								handleQuantityChange(e.target.value);
							}}
							onFocus={(e) => {
								e.target.select();
							}} // Auto-select all text when focused
							className="text-center text-sm w-[36px] h-[30px] outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none border-0 bg-transparent rounded-none focus:outline-none focus:ring-0 focus:border-0"
							style={{
								MozAppearance: 'textfield',
								border: 'none',
								boxShadow: 'none',
								borderRadius: '0',
							}}
							disabled={disabled}
							autoFocus={false}
						/>
						{/* Status icons removed per user request */}
					</div>
					<button
						onClick={incrementQuantity}
						className={`flex-1 h-8 flex items-center justify-center bg-transparent hover:bg-black/5 dark:hover:bg-white/10 transition-colors rounded-r-lg touch-manipulation`}
						disabled={false}
					>
						<Icon
							icon={faPlus}
							size={10}
							className={
								disabled
									? colors.classes.textSecondary
									: colors.classes.textPrimary
							}
						/>
					</button>
				</div>
			</div>
		);
	},
	(prevProps, nextProps) =>
		// Custom comparison to prevent unnecessary re-renders
		prevProps.disabled === nextProps.disabled &&
		prevProps.initialQuantity === nextProps.initialQuantity &&
		prevProps.maxQuantity === nextProps.maxQuantity &&
		prevProps.cartQuantity === nextProps.cartQuantity &&
		prevProps.clientId === nextProps.clientId &&
		prevProps.itemId === nextProps.itemId &&
		prevProps.price === nextProps.price &&
		prevProps.isCartLoading === nextProps.isCartLoading &&
		prevProps.onCartChange === nextProps.onCartChange &&
		prevProps.onCartUpdate === nextProps.onCartUpdate &&
		prevProps.addButtonText === nextProps.addButtonText &&
		prevProps.addingText === nextProps.addingText &&
		prevProps.getPendingPrice === nextProps.getPendingPrice,
);
